import { useEffect } from "react";
import HeroSection from "./components/HeroSection";
import StickyNav from "./components/StickyNav";
import ProcessingTimeSection from "./components/ProcessingTimeSection";
import ServicesSection from "./components/ServicesSection";
import LocalAreaSection from "./components/LocalAreaSection";
import TestimonialsSection from "./components/TestimonialsSection";
import AcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import ComparisonSection from "./components/ComparisonSection";
import MistakesSection from "./components/MistakesSection";
import HowItWorksSection from "./components/HowItWorksSection";
import FAQSection, { faqs } from "./components/FAQSection";
import HelpfulResourcesSection from "./components/HelpfulResourcesSection";
import FinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function ThorntonPage() {
  useEffect(() => {
    document.title = "Emergency Passport Thornton, CO — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/thornton/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Thornton, CO — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving north Denver metro families, business travelers, and students near DIA.");
    setMeta("geo.placename", "Thornton, Colorado");
    setMeta("geo.region", "US-CO");
    setMeta("geo.position", "39.8680;-104.9719");
    setMeta("ICBM", "39.8680, -104.9719");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Colorado", item: "https://fastpassportcenter.com/colorado/" },
          { "@type": "ListItem", position: 3, name: "Thornton Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services as fast as 24 hours for Thornton and north Denver metro travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Thornton", containedInPlace: { "@type": "State", name: "Colorado" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-thornton";
    document.getElementById("structured-data-thornton")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data-thornton")?.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <HeroSection />
      <StickyNav />
      <ProcessingTimeSection />
      <ServicesSection />
      <LocalAreaSection />
      <TestimonialsSection />
      <AcceptanceFacilitiesSection />
      <ComparisonSection />
      <MistakesSection />
      <HowItWorksSection />
      <FAQSection />
      <HelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/thornton" state="CO" />
      <FinalCTA />
    </div>
  );
}